// Le rapport opérateurs — ce que la journée dit de chacun.
//
// L'étape 05A réunit deux choses qui vivaient séparées et qui parlaient des
// mêmes personnes : le rapport opérateur, qu'on ne voyait qu'en marge du
// dépouillement, et les opérations à risque, qui avaient leur propre étape.
// Les tenir à part obligeait à faire le rapprochement de tête — « celui qui a
// 2,50 € d'écart, c'est bien celui qui a passé six annulations ? » — et c'est
// précisément ce rapprochement qui renseigne.
//
// CE FICHIER NE DÉCIDE RIEN, IL CONSTATE (P4). Il ne classe pas les gens : il
// range les FAITS de la journée sous chaque nom, et laisse l'écran les montrer.
//
// D14 s'applique partout : un écart n'est jamais vert et sa couleur ne dépend
// jamais de son signe. Un excédent de 4 € est la même anomalie qu'un manque de
// 4 €. Le vert ne dit qu'une chose — « il n'y a pas d'écart ».

import type { Rapport } from './types'

const tableau = (v: unknown): Array<Record<string, unknown>> =>
  Array.isArray(v) ? (v as Array<Record<string, unknown>>) : []
const txt = (v: unknown): string => (typeof v === 'string' ? v : v == null ? '' : String(v))
const nb = (v: unknown): number => (Number.isFinite(Number(v)) ? Number(v) : 0)
const cents = (euros: number): number => Math.round(euros * 100)

/**
 * Un automate n'a pas de nom : ORIKA lui en fabrique un, « OPE 710 (710) ».
 *
 * C'est le seul signal fiable — le numéro de caisse ne l'est pas. Mesuré sur
 * une semaine, les automates vont de la caisse 71 à la caisse 98, et un seuil
 * posé sur un échantillon d'un jour rangeait OPE 710 avec les caissiers.
 */
export const estAutomate = (nom: string): boolean => /^OPE[ _-]/.test(nom.trim())

export type Nature = 'declaration' | 'ecart' | 'prelevement'

export interface Alerte {
  nature: Nature
  /** Ce qui est constaté, en une phrase. Jamais une consigne. */
  texte: string
  /** Le chiffre en cause, en centimes — pour l'afficher à côté de sa cause. */
  cents?: number
}

/**
 * TROIS GENRES, et ils ne se comparent pas :
 *
 *   caissier  — une personne. Elle a un rapport, elle déclare, et son écart
 *               tolère quelques euros : elle manipule des billets à la main.
 *   automate  — une caisse en libre-service. Elle encaisse (elle a des
 *               règlements) mais ne déclare pas : on la vide.
 *   monnayeur — la machine à monnaie. Elle a SON PROPRE rapport, avec fonds,
 *               déclaration et écart, et l'import lui pose son propre drapeau
 *               d'anomalie. Elle compte au centime : chez elle, tout écart est
 *               une anomalie, quel que soit son montant.
 */
export type Genre = 'caissier' | 'automate' | 'monnayeur'

export interface Operateur {
  badge: string
  nom: string
  genre: Genre
  /** Un automate n'en a pas : il ne déclare pas, on le vide. */
  rapport: {
    fdc: number
    apport: number
    reglEsp: number
    prelev: number
    prelevCount: number
    prelevIrregulier: boolean
    theorique: number
    declare: number
    ecart: number
  } | null
  /** Les opérations à risque imputées à cette personne, par famille. */
  risque: { annulations: number; lignes: number; remises: number; prix: number; cents: number }
  /** Les autorisations qu'il a DEMANDÉES, et celles qu'il a DONNÉES. */
  autorisations: { demandees: number; donnees: number }
  /**
   * Ce qu'une machine à monnaie sait dire d'elle-même, et qu'aucun humain ne
   * peut dire de lui : combien de fois on y a inséré, combien elle a rendu.
   * C'est la mesure de son ACTIVITÉ, à côté de la mesure de sa justesse.
   */
  machine: {
    insertions: number
    especesInserees: number
    rendus: number
    montantRendu: number
    /** Le drapeau posé par l'import lui-même. */
    anomalie: boolean
    inventaire: string
  } | null
  alertes: Alerte[]
}

export interface Seuils {
  declaration: number
  ecartTolere: number
  ecartCritique: number
  prelevement: number
}

export const SEUILS_DEFAUT: Seuils = {
  declaration: 65000,
  ecartTolere: 200,
  ecartCritique: 500,
  prelevement: 50000,
}

/** Le badge d'une ligne d'opération, quel que soit le champ qui le porte. */
const badgeDe = (o: Record<string, unknown>, cle = 'operateur'): string => {
  const brut = txt(o[cle])
  // « ESSAMBA Thomas (6252) » → « 6252 ». Le badge est entre parenthèses en fin
  // de chaîne ; sans lui on ne peut rattacher la ligne à personne.
  const m = brut.match(/\((\d+)\)\s*$/)
  return m ? m[1]! : ''
}

/**
 * Tout ce que la journée dit de chaque opérateur, réuni sous son badge.
 *
 * Les monnayeurs y figurent AUSSI. Ils n'ont ni déclaration ni écart — une
 * machine ne déclare pas — et leur `rapport` est donc nul plutôt que rempli de
 * zéros : un zéro se lit « pas d'écart », ce qui serait faux là où il n'y a
 * rien à comparer.
 */
export function operateurs(r: Rapport | null, seuils: Seuils = SEUILS_DEFAUT): Operateur[] {
  const par = new Map<string, Operateur>()

  const obtenir = (badge: string, nom: string): Operateur | null => {
    if (!badge) return null
    let o = par.get(badge)
    if (!o) {
      o = {
        badge, nom: nom || `Badge ${badge}`,
        genre: estAutomate(nom) ? 'automate' : 'caissier',
        rapport: null,
        risque: { annulations: 0, lignes: 0, remises: 0, prix: 0, cents: 0 },
        autorisations: { demandees: 0, donnees: 0 },
        machine: null,
        alertes: [],
      }
      par.set(badge, o)
    } else if (nom && o.nom.startsWith('Badge ')) {
      o.nom = nom
      if (o.genre !== 'monnayeur') o.genre = estAutomate(nom) ? 'automate' : 'caissier'
    }
    return o
  }

  // ── 1 · Le rapport opérateur : les caissiers qui déclarent ───────────────
  for (const l of tableau(r?.rapportOps)) {
    const o = obtenir(txt(l.badge), txt(l.nom))
    if (!o) continue
    o.rapport = {
      fdc: cents(nb(l.fdc)),
      apport: cents(nb(l.apport)),
      reglEsp: cents(nb(l.reglEsp)),
      prelev: cents(nb(l.prelev)),
      prelevCount: nb(l.prelevCount),
      prelevIrregulier: l.prelevIrregular === true,
      theorique: cents(nb(l.theorique)),
      declare: cents(nb(l.decl)),
      ecart: cents(nb(l.diff)),
    }
  }

  // ── 1 bis · Les monnayeurs, qui viennent du fichier importé ─────────────
  //
  // Leur ligne a exactement la forme de celle d'un caissier — fonds, apport,
  // règlements espèces, théorique, déclaré, écart — plus ce qu'une machine est
  // seule à savoir dire d'elle : ses insertions et ses rendus.
  //
  // Je les avais d'abord reconstruits depuis `apportsSco`, que FlashCP écrit
  // lui-même à l'étape 02A. C'était la mauvaise source : elle ne dit que ce
  // qu'ON A MIS dedans, pas ce que la machine a compté.
  for (const m of tableau(r?.monnayeurs)) {
    const id = txt(m.id)
    if (!id) continue
    const o = obtenir(id, `Monnayeur ${id}`)
    if (!o) continue
    o.genre = 'monnayeur'
    o.nom = `Monnayeur ${id}`
    o.rapport = {
      fdc: cents(nb(m.fdc)),
      apport: cents(nb(m.apport)),
      reglEsp: cents(nb(m.reglEsp)),
      prelev: cents(nb(m.prelev)),
      prelevCount: nb(m.prelevCount),
      prelevIrregulier: false,
      theorique: cents(nb(m.theorique)),
      declare: cents(nb(m.decl)),
      ecart: cents(nb(m.diff)),
    }
    o.machine = {
      insertions: nb(m.nbInsertions),
      especesInserees: cents(nb(m.especesInseres)),
      rendus: nb(m.nbRendu),
      montantRendu: cents(nb(m.mntRendu)),
      anomalie: m.hasAnomaly === true,
      inventaire: txt(m.heureInventaire),
    }
  }

  // ── 2 · Les règlements : de quoi rattacher les automates ─────────────────
  for (const p of tableau(r?.payments)) {
    obtenir(txt(p.numeroOperateur), txt(p.operateur))
  }

  // ── 3 · Les opérations à risque, imputées à qui les a passées ────────────
  const compter = (source: unknown, champ: keyof Operateur['risque']) => {
    for (const l of tableau(source)) {
      const o = obtenir(badgeDe(l), txt(l.operateur))
      if (!o) continue
      ;(o.risque[champ] as number) += 1
      o.risque.cents += Math.abs(cents(nb(l.valeur ?? l.montant ?? l.prixVente)))
    }
  }
  compter(r?.annules, 'annulations')
  compter(r?.annulationsLigne, 'lignes')
  compter(r?.remises, 'remises')
  compter(r?.changementsPrix, 'prix')

  // Une autorisation a DEUX personnes : celle qui demande et celle qui autorise.
  // N'en compter qu'une reviendrait à ne poser la question qu'à moitié.
  for (const l of tableau(r?.autorisations)) {
    const qui = obtenir(badgeDe(l), txt(l.operateur))
    if (qui) qui.autorisations.demandees += 1
    const chef = obtenir(badgeDe(l, 'superviseur'), txt(l.superviseur))
    if (chef) chef.autorisations.donnees += 1
  }

  // ── 4 · Les alertes, greffées sur le fait qui les provoque ───────────────
  for (const o of par.values()) {
    const rap = o.rapport
    if (rap) {
      // LE SEUIL DE DÉCLARATION NE VAUT QUE POUR UN CAISSIER.
      //
      // 650 € mesure ce qu'une PERSONNE garde en espèces dans son tiroir. Un
      // monnayeur en contient deux à trois mille par construction : lui
      // appliquer la règle allumait les quatre machines tous les jours, et
      // quatre alertes quotidiennes qui ne veulent rien dire apprennent à ne
      // plus regarder la colonne.
      if (o.genre === 'caissier' && rap.declare > seuils.declaration) {
        o.alertes.push({
          nature: 'declaration',
          texte: `déclaration au-dessus du seuil`,
          cents: rap.declare,
        })
      }
      // LE SEUIL D'ÉCART N'EST PAS LE MÊME POUR UNE MACHINE.
      //
      // La tolérance de 2 € existe parce qu'un humain compte des billets à la
      // main. Un monnayeur compte au centime : chez lui, 1,30 € d'écart n'est
      // pas « dans les clous », c'est une anomalie — et l'import le dit déjà
      // lui-même avec `hasAnomaly`. Lui appliquer la tolérance d'un caissier
      // aurait fait passer sous silence deux anomalies sur vingt lignes.
      const seuilEcart = o.genre === 'monnayeur' ? 1 : seuils.ecartTolere
      if (Math.abs(rap.ecart) >= seuilEcart || o.machine?.anomalie === true) {
        o.alertes.push({
          nature: 'ecart',
          // Le mot ne dépend pas du signe : c'est la même anomalie (D14).
          texte: Math.abs(rap.ecart) >= seuils.ecartCritique ? 'écart critique' : 'écart',
          cents: rap.ecart,
        })
      }
      if (rap.prelevIrregulier) {
        o.alertes.push({
          nature: 'prelevement',
          texte: `un prélèvement ne fait pas ${seuils.prelevement / 100} €`,
          cents: rap.prelev,
        })
      }
    }
    // LES OPÉRATIONS À RISQUE SE MONTRENT, ELLES N'ALERTENT PAS.
    //
    // Sur une journée réelle — 772 opérations — vingt-deux personnes sur
    // soixante-deux en ont au moins une. Les compter comme des alertes faisait
    // dire « 26 sur 62 demandent un regard » là où sept anomalies véritables
    // existaient, et un « à regarder » qui désigne un tiers du magasin ne
    // désigne plus rien.
    //
    // Une annulation n'est pas une faute : c'est le travail normal d'une
    // caisse. L'ancienne étape 09 était une REVUE, pas une liste d'alertes.
    // Le compte reste sur la ligne, et le filtre « Opérations » permet d'y
    // aller — mais il n'entre pas dans le décompte de ce qui cloche.
  }

  return [...par.values()]
}

/**
 * L'ordre d'affichage : ce qui réclame un regard d'abord.
 *
 * On ne trie PAS par montant d'écart. Un écart de 2,50 € et six annulations à
 * 400 € ne se comparent pas sur une même échelle, et prétendre le contraire
 * ferait un classement qui a l'air objectif et ne l'est pas. On trie par NOMBRE
 * d'anomalies — combien de choses distinctes clochent chez cette personne —
 * puis par badge, qui est stable.
 */
export function parUrgence(a: Operateur, b: Operateur): number {
  if (a.alertes.length !== b.alertes.length) return b.alertes.length - a.alertes.length
  // Les personnes d'abord, puis les machines : on commence par qui on peut aller voir.
  const rang = (g: Genre) => (g === 'caissier' ? 0 : g === 'monnayeur' ? 1 : 2)
  if (rang(a.genre) !== rang(b.genre)) return rang(a.genre) - rang(b.genre)
  return (Number(a.badge) || 0) - (Number(b.badge) || 0)
}

/** Le compte de la journée, pour la ligne de tête. */
export function bilan(l: Operateur[]): {
  personnes: number
  monnayeurs: number
  automates: number
  avecAlerte: number
  parNature: Record<Nature, number>
} {
  const parNature: Record<Nature, number> = { declaration: 0, ecart: 0, prelevement: 0 }
  for (const o of l) for (const a of o.alertes) parNature[a.nature]++
  return {
    personnes: l.filter((o) => o.genre === 'caissier').length,
    monnayeurs: l.filter((o) => o.genre === 'monnayeur').length,
    automates: l.filter((o) => o.genre === 'automate').length,
    avecAlerte: l.filter((o) => o.alertes.length > 0).length,
    parNature,
  }
}

// ── Ce que le bilan du jour ne disait pas ──────────────────────────────────

export interface Constat {
  /** Une phrase en français, avant tout chiffre. */
  phrase: string
  /**
   * L'écart NET et l'écart BRUT.
   *
   * Le net additionne les signes, le brut additionne les valeurs absolues. Un
   * net de +0,50 € sur un brut de 46,30 € ne décrit pas une journée plate : il
   * décrit sept écarts qui se compensent, ce qui n'est pas du tout la même
   * chose et ne se voit sur aucun total.
   */
  net: number
  brut: number
  /**
   * LE CANARI. Si le déclaré moins le théorique ne redonne pas l'écart, c'est
   * le FICHIER qui est douteux, pas la caisse. Colorer un écart faux serait la
   * pire façon de respecter D14 : on afficherait une anomalie inventée avec
   * l'autorité d'une mesure. Dans ce cas on se tait sur les écarts et on le dit.
   */
  colonnesSuspectes: number
}

export function constat(l: Operateur[], seuils: Seuils = SEUILS_DEFAUT): Constat {
  const avec = l.filter((o) => o.rapport)
  let net = 0
  let brut = 0
  let suspectes = 0

  for (const o of avec) {
    const r = o.rapport!
    net += r.ecart
    brut += Math.abs(r.ecart)
    // Un centime de tolérance : les montants viennent d'un fichier en euros.
    if (Math.abs(r.declare - r.theorique - r.ecart) > 1) suspectes++
  }

  const parNature = bilan(l).parNature
  const aRegarder = l.filter((o) => o.alertes.length > 0).length
  const bouts: string[] = []
  if (parNature.ecart > 0) bouts.push(`${parNature.ecart} écart${parNature.ecart > 1 ? 's' : ''}`)
  if (parNature.declaration > 0) {
    bouts.push(`${parNature.declaration} déclaration${parNature.declaration > 1 ? 's' : ''} au-dessus de ${Math.round(seuils.declaration / 100)} €`)
  }
  if (parNature.prelevement > 0) {
    bouts.push(`${parNature.prelevement} prélèvement${parNature.prelevement > 1 ? 's' : ''} hors 500 €`)
  }

  const phrase = aRegarder === 0
    ? `Personne ne demande de regard aujourd’hui — ${l.length} opérateurs, aucune anomalie.`
    : `${aRegarder} sur ${l.length} demandent un regard : ${bouts.join(', ')}.`

  return { phrase, net, brut, colonnesSuspectes: suspectes }
}
